import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import {
  useSurveySetup,
  useSurveysIsRunningSearchInline,
} from "@/queries/surveys/use-surveys.query.ts";
import { useWorkflowUpdateBlockActionSurveyEnroll } from "@/queries/workflows/workflow.query.ts";
import { Rule, RuleConditionType, RuleConditionsItems } from "@/types/rule.types.ts";
import {
  ApiWorkflowBlockActionSavePayload,
  ApiWorkflowBlockActionSurveyEnrollSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Avatar,
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionSurveyEnrollModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
}

interface WorkflowEditActionSurveyEnrollFormData extends WorkflowActionTimingFormFields {
  title: string | null;
  survey_id: number | null;
}

export const WorkflowEditActionSurveyEnrollModal = ({
  workflowVersion,
  block,
  ...props
}: WorkflowEditActionSurveyEnrollModalProps) => {
  const { close } = useModals();
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const { id } = { ...props };
  const [activeTab, setActiveTab] = useState(0);
  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    options: surveyOptions,
    search,
    isFetching,
    isLoading,
  } = useSurveysIsRunningSearchInline(true);

  const schema = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    survey_id: yup
      .number()
      .required(t("errors:field_required"))
      .typeError(t("errors:field_required"))
      .nullable(),
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<WorkflowEditActionSurveyEnrollFormData>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      survey_id: block.action_survey_enroll?.survey_id ?? null,
    },
  });

  const updateBlockAction = useWorkflowUpdateBlockActionSurveyEnroll(control);

  const selectedSurveyId = useWatch({ name: "survey_id", control });

  const { data: selectedSurvey } = useSurveySetup(selectedSurveyId ?? undefined);

  const hasSelectedSurveyOption = useMemo(() => {
    if (selectedSurveyId == null) return true;
    return surveyOptions.some((item) => Number(item.id) === selectedSurveyId);
  }, [selectedSurveyId, surveyOptions]);

  const surveySelectOptions = useMemo(() => {
    if (!selectedSurvey || hasSelectedSurveyOption || selectedSurveyId == null)
      return surveyOptions;
    return [{ id: selectedSurveyId, title: selectedSurvey.title } as SelectItem, ...surveyOptions];
  }, [selectedSurvey, hasSelectedSurveyOption, selectedSurveyId, surveyOptions]);

  const participantRuleTypeTitles = useMemo(() => {
    return new Map(RuleConditionsItems().map((item) => [String(item.id), item.title]));
  }, [t]);

  const transformableParticipantsRuleTitles = useMemo(() => {
    const rules = selectedSurvey?.participants_rules ?? [];
    const titles: string[] = [];
    const usedTitles = new Set<string>();

    for (const rule of rules) {
      if (rule.condition_type === RuleConditionType.employee) continue;

      const title =
        participantRuleTypeTitles.get(String(rule.condition_type)) ?? String(rule.condition_type);

      if (usedTitles.has(title)) continue;
      usedTitles.add(title);
      titles.push(title);
    }

    return titles;
  }, [participantRuleTypeTitles, selectedSurvey?.participants_rules]);

  const hasOnlyEmployeeParticipantsRules = useMemo(() => {
    const rules = selectedSurvey?.participants_rules ?? [];
    if (!rules.length) return false;
    return rules.every((rule: Rule) => rule.condition_type === RuleConditionType.employee);
  }, [selectedSurvey?.participants_rules]);

  const shouldShowRulesConversionWarning = Boolean(
    (selectedSurvey?.participants_rules?.length ?? 0) > 0 && !hasOnlyEmployeeParticipantsRules
  );

  const handleSave = handleSubmit(async (data) => {
    if (selectedSurveyId == null) return;

    try {
      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          conditions: conditions.map((condition) => ({ ...condition })),
          block_data: { survey_id: selectedSurveyId } as ApiWorkflowBlockActionSurveyEnrollSave,
        } as ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionSurveyEnrollSave>,
      });
      close(id);
    } catch {
      // handled by hook
    }
  });

  const isSubmitDisabled =
    selectedSurveyId == null || !selectedSurvey || updateBlockAction.isPending;

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_survey_enroll")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <Button
            className={"w-full"}
            size={"lg"}
            type={"submit"}
            onClick={handleSave}
            isLoading={updateBlockAction.isPending}
            disabled={isSubmitDisabled}
          >
            {t("common:save")}
          </Button>
          <Button className={"w-full"} size={"lg"} variant={"secondary"} onClick={() => close(id)}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("surveys:survey")}>
            <Stack className={"py-6"}>
              <Controller
                control={control}
                name={"survey_id"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("surveys:survey")}
                    value={value}
                    emptyTitle={t("common:select")}
                    searchable
                    required
                    loadOptionsOnOpen
                    onSearchOptions={search}
                    searchOptions={surveySelectOptions}
                    onChange={(nextValue) => {
                      const normalizedValue =
                        nextValue == null || nextValue === "" ? null : Number(nextValue);
                      setValue("survey_id", normalizedValue, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      onChange(normalizedValue);
                    }}
                    isLoading={isLoading || isFetching}
                    error={errors.survey_id?.message}
                  />
                )}
              />
              {shouldShowRulesConversionWarning && (
                <Box className={"mt-6 bg-dark/5 border-dark/5 border rounded-xs p-5"}>
                  <Stack gap={"md"}>
                    <Avatar emoji={":information_source:"} size={"md"} />
                    <Title
                      header={t("workflow:survey_enroll_rules_conversion_warning_title", {
                        rules: transformableParticipantsRuleTitles.join(", "),
                      })}
                      size={"sm"}
                    />
                    <Title
                      header={t("workflow:survey_enroll_rules_conversion_warning_header")}
                      caption={t("workflow:survey_enroll_rules_conversion_warning_caption")}
                      size={"sm"}
                    />
                  </Stack>
                </Box>
              )}
            </Stack>
          </TabItem>

          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("title")}
                  error={errors.title?.message}
                />
              </Stack>

              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
